import { useMemo, useState } from 'react'
import queryString from 'query-string'
import useShowError from 'hooks/useShowError'
import utils from 'helpers/utils'
import axios from 'providers/axios'
import { useLocation, useNavigate } from 'react-router-dom'
import useEffectOnce from './useEffectOnce'
import { URL_APP } from 'helpers/config'
import { GoogleStateObject } from 'helpers/integrations/google'

const useGoogleAuth = () => {
  const showError = useShowError()
  const navigate = useNavigate()
  const location = useLocation()
  const [message, setMessage] = useState('Loading...')
  const [loading, setLoading] = useState(true)

  const urlParams = useMemo(() => {
    let redirectUrl = '/'
    let typeCreationApp = ''
    let requester = ''
    let overrideDefaultSignupAction: string | undefined = undefined
    let productId: string | undefined = undefined
    const { state, code } = queryString.parse(location.search)
    try {
      const parsedState = JSON.parse(state as string) as GoogleStateObject
      redirectUrl = parsedState.redirectUrl
      typeCreationApp = parsedState.typeCreationApp
      productId = parsedState.productId
      requester = parsedState.requester || ''
      overrideDefaultSignupAction = parsedState.overrideDefaultSignupAction
    } catch (error) {
      showError(error)
    }
    return { state, code, redirectUrl, typeCreationApp, productId, requester, overrideDefaultSignupAction }
  }, [location.search, showError])

  useEffectOnce(() => {
    const { state, code, redirectUrl, typeCreationApp, productId, requester, overrideDefaultSignupAction } = urlParams
    const isChromeExtension = requester === 'chrome-extension'

    const login = async () => {
      try {
        const response = await axios.post('/google/login', {
          code,
          authDevice: utils.getFullAuthDeviceData(),
          redirectUrl,
        })
        setLoading(false)
        setMessage('Redirecting...')
        if (!response?.data) throw new Error('Bad response')
        const { authDevice } = response.data
        utils.setChromeExtension(authDevice.deviceToken)

        if (isChromeExtension) return

        window.location.href = response.data.redirectUrl
      } catch (error) {
        showError(error)
        navigate('/login')
      }
    }

    const signUp = async () => {
      try {
        const response = await axios.post('/google/sign-up', {
          code,
          authDevice: utils.getFullAuthDeviceData(),
          productId,
          overrideDefaultSignupAction,
        })
        setLoading(false)
        setMessage('Redirecting...')
        if (!response?.data) throw new Error('Bad response')
        const { authDevice } = response.data
        utils.setChromeExtension(authDevice.deviceToken)

        if (isChromeExtension) return

        window.location.href = URL_APP
      } catch (error) {
        showError(error)
        navigate('/signup')
      }
    }

    if (!code || !state || !typeCreationApp) return navigate('/login')

    if (typeCreationApp === 'LOGIN') login()
    if (typeCreationApp === 'SIGN_UP') signUp()
  })

  return { message, loading }
}

export default useGoogleAuth
